import { BarChart3 } from 'lucide-react';
import React, { useMemo } from 'react';

export interface AdminChartSeries {
  labels: string[];
  data: number[];
}

interface AdminBarChartProps {
  data: AdminChartSeries;
  color?: string;
  height?: number;
  emptyMessage?: string;
  emptyHint?: string;
  /** When true, renders the numeric value above each non-zero bar. */
  showValues?: boolean;
  formatValue?: (value: number) => string;
}

function chartHasValues(data: AdminChartSeries): boolean {
  return data.labels.length > 0 && data.data.some((v) => Number(v) > 0);
}

function axisMaxFor(maxVal: number): number {
  if (maxVal <= 0) return 1;
  if (maxVal <= 5) return Math.max(maxVal, 5);
  if (maxVal <= 10) return Math.ceil(maxVal / 5) * 5;
  return Math.ceil(maxVal / 10) * 10;
}

/**
 * Compact bar chart for Admin Analytics — no nested headers, no absolute
 * overflow (prevents grid cards from overlapping).
 */
const defaultFormatValue = (value: number): string =>
  Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export const AdminBarChart: React.FC<AdminBarChartProps> = ({
  data,
  color = '#6C47FF',
  height = 200,
  emptyMessage = 'No data for this period.',
  emptyHint,
  showValues = false,
  formatValue = defaultFormatValue,
}) => {
  const hasData = chartHasValues(data);
  const maxVal = useMemo(
    () => (data.data.length > 0 ? Math.max(...data.data.map((v) => Number(v) || 0)) : 0),
    [data.data],
  );
  const axisMax = axisMaxFor(maxVal);

  const yTicks = useMemo(() => {
    const step = axisMax <= 5 ? 1 : axisMax <= 20 ? 2 : Math.ceil(axisMax / 5);
    const ticks: number[] = [];
    for (let v = axisMax; v >= 0; v -= step) ticks.push(v);
    if (ticks[ticks.length - 1] !== 0) ticks.push(0);
    return ticks;
  }, [axisMax]);

  if (!hasData) {
    return (
      <div
        className="flex flex-col items-center justify-center rounded-xl border text-center px-4"
        style={{
          height: height + 48,
          borderColor: 'var(--vscode-border-secondary)',
          background: 'var(--vscode-bg-secondary, rgba(255,255,255,0.03))',
          color: 'var(--vscode-text-tertiary)',
        }}
      >
        <BarChart3 className="w-10 h-10 mb-2 opacity-50" aria-hidden />
        <p className="text-[13px] font-medium">{emptyMessage}</p>
        {emptyHint ? (
          <p className="text-[11px] mt-1 max-w-[280px] opacity-80">{emptyHint}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex gap-2 w-full min-w-0"
      style={{ height: height + 48 }}
      role="img"
      aria-label="Bar chart"
    >
      {/* Y-axis */}
      <div
        className="flex w-8 shrink-0 flex-col justify-between py-2 text-[10px] tabular-nums"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        {yTicks.map((tick) => (
          <span key={tick} className="text-right leading-none">
            {tick}
          </span>
        ))}
      </div>

      {/* Plot */}
      <div
        className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border"
        style={{
          borderColor: 'var(--vscode-border-secondary)',
          background: 'var(--vscode-bg-secondary, rgba(255,255,255,0.02))',
        }}
      >
        {/* Grid lines */}
        <div className="pointer-events-none absolute inset-0 flex flex-col justify-between py-2">
          {yTicks.map((tick) => (
            <div
              key={`grid-${tick}`}
              className="w-full border-t"
              style={{ borderColor: 'var(--vscode-border-secondary)', opacity: 0.5 }}
            />
          ))}
        </div>

        {/* Bars */}
        <div
          className={`relative z-[1] flex flex-1 items-end justify-between gap-0.5 px-2 pb-7 ${showValues ? 'pt-1' : 'pt-3'}`}
          style={{ height }}
        >
          {data.labels.map((label, index) => {
            const value = Number(data.data[index] ?? 0);
            const pct = axisMax > 0 ? Math.max(0, (value / axisMax) * 100) : 0;
            const barH = value > 0 ? Math.max(pct, 4) : 0;

            return (
              <div
                key={`${label}-${index}`}
                className="flex h-full min-w-0 flex-1 flex-col items-center justify-end"
                title={`${label}: ${value}`}
              >
                {showValues && value > 0 ? (
                  <span
                    className="mb-0.5 max-w-full truncate px-0.5 text-[9px] font-semibold tabular-nums leading-none"
                    style={{ color: 'var(--vscode-text-secondary)' }}
                    aria-hidden
                  >
                    {formatValue(value)}
                  </span>
                ) : null}
                <div
                  className="w-full max-w-[40px] rounded-t-md transition-[height] duration-500 ease-out"
                  style={{
                    height: `${barH}%`,
                    minHeight: value > 0 ? 4 : 0,
                    background: value > 0
                      ? `linear-gradient(180deg, ${color} 0%, color-mix(in srgb, ${color} 65%, transparent) 100%)`
                      : 'var(--vscode-border-secondary)',
                    opacity: value > 0 ? 1 : 0.35,
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* X-axis labels — inside plot, no negative offset */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between gap-0.5 px-2 pb-1.5">
          {data.labels.map((label, index) => (
            <span
              key={`lbl-${label}-${index}`}
              className="min-w-0 flex-1 truncate text-center text-[10px] leading-tight"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export { chartHasValues };
